/* eslint-disable no-unused-vars */

import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";

import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Tooltip from "@material-ui/core/Tooltip";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import TablePagination from "@material-ui/core/TablePagination";

import AddIcon from "@material-ui/icons/Add";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import DescriptionIcon from "@material-ui/icons/Description";
import PlayCircleOutlineIcon from "@material-ui/icons/PlayCircleOutline";
import PauseCircleOutlineIcon from "@material-ui/icons/PauseCircleOutline";
import RepeatIcon from "@material-ui/icons/Repeat";
import StopIcon from "@material-ui/icons/Stop";
import ScheduleIcon from "@material-ui/icons/Schedule";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import SendIcon from "@material-ui/icons/Send";

import MainContainer from "../../components/MainContainer";
import { PageHeader, StatusBadge } from "../../components/ui";
import EmptyState from "../../components/EmptyState";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import CampaignModal from "../../components/CampaignModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { isArray } from "lodash";
import { useDate } from "../../hooks/useDate";
import ForbiddenPage from "../../components/ForbiddenPage";
import usePlans from "../../hooks/usePlans";
import { AuthContext } from "../../context/Auth/AuthContext";
import { getTokens } from "../../styles/tokens";

const reducer = (state, action) => {
  if (action.type === "LOAD_CAMPAIGNS") {
    const campaigns = action.payload;
    const newCampaigns = [];

    if (isArray(campaigns)) {
      campaigns.forEach((campaign) => {
        const campaignIndex = state.findIndex((u) => u.id === campaign.id);
        if (campaignIndex !== -1) {
          state[campaignIndex] = campaign;
        } else {
          newCampaigns.push(campaign);
        }
      });
    }

    return [...state, ...newCampaigns];
  }

  if (action.type === "UPDATE_CAMPAIGNS") {
    const campaign = action.payload;
    const campaignIndex = state.findIndex((u) => u.id === campaign.id);

    if (campaignIndex !== -1) {
      state[campaignIndex] = campaign;
      return [...state];
    } else {
      return [campaign, ...state];
    }
  }

  if (action.type === "DELETE_CAMPAIGN") {
    const campaignId = action.payload;

    const campaignIndex = state.findIndex((u) => u.id === campaignId);
    if (campaignIndex !== -1) {
      state.splice(campaignIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    filterBar: {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      gap: 12,
      marginBottom: 16,
    },
    search: {
      width: "min(320px, 100%)",
      [theme.breakpoints.down("xs")]: { width: "100%" },
    },
    searchIcon: { color: t.textMuted, fontSize: 20 },
    filterSelect: {
      width: 210,
      [theme.breakpoints.down("xs")]: { width: "100%" },
    },
    mainPaper: {
      flex: 1,
      minHeight: 0,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      boxShadow: t.shadow.card,
    },
    tableWrap: {
      flex: 1,
      minHeight: 0,
      overflow: "auto",
      ...theme.scrollbarStyles,
      "& .MuiTable-root": { minWidth: 1120 },
      // a rolagem é do cartão: o cabeçalho acompanha
      "& .MuiTableCell-head": { position: "sticky", top: 0, zIndex: 2, whiteSpace: "nowrap" },
    },
    nameCell: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      fontWeight: 600,
      color: t.text,
    },
    inlineRow: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      whiteSpace: "nowrap",
    },
    inlineIcon: { fontSize: 16, color: t.textMuted },
    recurrenceStack: {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      gap: 4,
    },
    mutedMeta: {
      color: t.textMuted,
      fontSize: 12,
    },
    muted: { color: t.textMuted },
    nowrap: { whiteSpace: "nowrap" },
    actionGroup: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "flex-end",
      gap: 2,
      whiteSpace: "nowrap",
    },
    menuIcon: { minWidth: 32, color: t.textSecondary },
    menuDanger: {
      color: t.semantic.error,
      "& .MuiListItemIcon-root": { color: t.semantic.error },
    },
    footer: {
      flex: "none",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: 8,
      padding: "4px 8px 4px 16px",
      borderTop: "1px solid " + t.line,
      backgroundColor: t.paper,
    },
    footerTotal: {
      fontSize: 13,
      color: t.textSecondary,
    },
  };
});

const Campaigns = () => {
  const classes = useStyles();
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [deletingCampaign, setDeletingCampaign] = useState(null);
  const [campaignModalOpen, setCampaignModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [stopRecurrenceModalOpen, setStopRecurrenceModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [recurrenceFilter, setRecurrenceFilter] = useState("");
  const [campaigns, dispatch] = useReducer(reducer, []);
  // menu "⋮" da linha: guarda a campanha junto com a âncora
  const [rowMenu, setRowMenu] = useState({ anchorEl: null, campaign: null });
  const { user, socket } = useContext(AuthContext);

  const { datetimeToClient } = useDate();
  const { getPlanCompany } = usePlans();

  useEffect(() => {
    async function fetchData() {
      const companyId = user.companyId;
      const planConfigs = await getPlanCompany(undefined, companyId);
      if (!planConfigs.plan.useCampaigns) {
        toast.error("Esta empresa não possui permissão para acessar essa página! Estamos lhe redirecionando.");
        setTimeout(() => {
          history.push(`/`)
        }, 1000);
      }
    }
    fetchData();
  }, []);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam, statusFilter, recurrenceFilter, pageSize]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchCampaigns();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber, statusFilter, recurrenceFilter, pageSize]);

  useEffect(() => {
    const companyId = user.companyId;

    const onCompanyCampaign = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_CAMPAIGNS", payload: data.record });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_CAMPAIGN", payload: +data.id });
      }
    }

    socket.on(`company-${companyId}-campaign`, onCompanyCampaign);
    return () => {
      socket.off(`company-${companyId}-campaign`, onCompanyCampaign);
    };
  }, [user]);

  const fetchCampaigns = async () => {
    try {
      const { data } = await api.get("/campaigns/", {
        params: { 
          searchParam, 
          pageNumber, 
          pageSize,
          status: statusFilter,
          isRecurring: recurrenceFilter 
        },
      });
      
      // Para paginação real, substituir os dados em vez de adicionar
      if (pageNumber === 1) {
        dispatch({ type: "RESET" });
      }
      dispatch({ type: "LOAD_CAMPAIGNS", payload: data.records });
      
      setHasMore(data.hasMore);
      setTotalPages(data.totalPages || 1);
      setTotalCount(data.count || 0);
      setLoading(false);
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenCampaignModal = () => {
    setSelectedCampaign(null);
    setCampaignModalOpen(true);
  };

  const handleCloseCampaignModal = () => {
    setSelectedCampaign(null);
    setCampaignModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
  };

  const handleRecurrenceFilterChange = (event) => {
    setRecurrenceFilter(event.target.value);
  };

  const handleEditCampaign = (campaign) => {
    setSelectedCampaign(campaign);
    setCampaignModalOpen(true);
  };

  const handleDeleteCampaign = async (campaignId) => {
    try {
      await api.delete(`/campaigns/${campaignId}`);
      toast.success(i18n.t("campaigns.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingCampaign(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const handleStopRecurrence = async (campaignId) => {
    try {
      await api.post(`/campaigns/${campaignId}/stop-recurrence`);
      toast.success("Recorrência interrompida com sucesso!");
      setPageNumber(1);
      fetchCampaigns();
    } catch (err) {
      toastError(err);
    }
    setStopRecurrenceModalOpen(false);
    setSelectedCampaign(null);
  };

  // mantém a campanha até o menu terminar de fechar (evita os itens sumirem na animação)
  const closeRowMenu = () => setRowMenu((prev) => ({ ...prev, anchorEl: null }));

  const handlePageChange = (event, newPage) => {
    setPageNumber(newPage + 1); // Material-UI usa índice baseado em 0
  };

  const handlePageSizeChange = (event) => {
    setPageSize(parseInt(event.target.value, 10));
    setPageNumber(1);
  };

  const formatStatus = (val) => {
    switch (val) {
      case "INATIVA":
        return "Inativa";
      case "PROGRAMADA":
        return "Programada";
      case "EM_ANDAMENTO":
        return "Em Andamento";
      case "CANCELADA":
        return "Cancelada";
      case "FINALIZADA":
        return "Finalizada";
      default:
        return val;
    }
  };

  const getStatusTone = (status) => {
    switch (status) {
      case "PROGRAMADA":
        return "info";
      case "EM_ANDAMENTO":
        return "warning";
      case "CANCELADA":
        return "error";
      case "FINALIZADA":
        return "success";
      default:
        return "neutral";
    }
  };

  const formatRecurrenceType = (type) => {
    switch (type) {
      case "minutely":
        return "Por Minuto";
      case "hourly":
        return "Por Hora";
      case "daily":
        return "Diário";
      case "weekly":
        return "Semanal";
      case "biweekly":
        return "Quinzenal";
      case "monthly":
        return "Mensal";
      case "yearly":
        return "Anual";
      default:
        return type;
    }
  };

  const cancelCampaign = async (campaign) => {
    try {
      await api.post(`/campaigns/${campaign.id}/cancel`);
      toast.success(i18n.t("campaigns.toasts.cancel"));
      setPageNumber(1);
      fetchCampaigns();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const restartCampaign = async (campaign) => {
    try {
      await api.post(`/campaigns/${campaign.id}/restart`);
      toast.success(i18n.t("campaigns.toasts.restart"));
      setPageNumber(1);
      fetchCampaigns();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <MainContainer maxWidth>
      <ConfirmationModal
        title={
          deletingCampaign &&
          `${i18n.t("campaigns.confirmationModal.deleteTitle")} ${deletingCampaign.name}?`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteCampaign(deletingCampaign.id)}
      >
        {i18n.t("campaigns.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      <ConfirmationModal
        title="Interromper Recorrência"
        open={stopRecurrenceModalOpen}
        onClose={() => setStopRecurrenceModalOpen(false)}
        onConfirm={() => handleStopRecurrence(selectedCampaign?.id)}
      >
        Tem certeza que deseja interromper a recorrência desta campanha? 
        A campanha atual continuará sendo executada, mas não haverão mais execuções futuras.
      </ConfirmationModal>

      {campaignModalOpen && (
        <CampaignModal
          resetPagination={() => {
            setPageNumber(1);
            fetchCampaigns();
          }}
          open={campaignModalOpen}
          onClose={handleCloseCampaignModal}
          aria-labelledby="form-dialog-title"
          campaignId={selectedCampaign && selectedCampaign.id}
        />
      )}
      {
        user.profile === "user" && user?.showCampaign === "disabled" ?
          <ForbiddenPage />
          :
          <>
            <PageHeader
              title={i18n.t("campaigns.title")}
              badge={<StatusBadge color="neutral" dot={false} label={String(totalCount)} />}
              actions={
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={handleOpenCampaignModal}
                >
                  {i18n.t("campaigns.buttons.add")}
                </Button>
              }
            />

            <div className={classes.filterBar}>
              <TextField
                className={classes.search}
                variant="outlined"
                size="small"
                placeholder={i18n.t("campaigns.searchPlaceholder")}
                type="search"
                value={searchParam}
                onChange={handleSearch}
                inputProps={{ "aria-label": i18n.t("campaigns.searchPlaceholder") }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon className={classes.searchIcon} />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                select
                className={classes.filterSelect}
                variant="outlined"
                size="small"
                value={statusFilter}
                onChange={handleStatusFilterChange}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (value) => (value ? formatStatus(value) : "Todos os status"),
                }}
                inputProps={{ "aria-label": "Filtrar por Status" }}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="INATIVA">Inativa</MenuItem>
                <MenuItem value="PROGRAMADA">Programada</MenuItem>
                <MenuItem value="EM_ANDAMENTO">Em Andamento</MenuItem>
                <MenuItem value="CANCELADA">Cancelada</MenuItem>
                <MenuItem value="FINALIZADA">Finalizada</MenuItem>
              </TextField>
              <TextField
                select
                className={classes.filterSelect}
                variant="outlined"
                size="small"
                value={recurrenceFilter}
                onChange={handleRecurrenceFilterChange}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (value) =>
                    value === "true" ? "Recorrentes" : value === "false" ? "Únicas" : "Todas as recorrências",
                }}
                inputProps={{ "aria-label": "Filtrar por Recorrência" }}
              >
                <MenuItem value="">Todas</MenuItem>
                <MenuItem value="true">Recorrentes</MenuItem>
                <MenuItem value="false">Únicas</MenuItem>
              </TextField>
            </div>

            <Paper className={classes.mainPaper} variant="outlined">
              <div className={classes.tableWrap}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{i18n.t("campaigns.table.name")}</TableCell>
                      <TableCell>{i18n.t("campaigns.table.status")}</TableCell>
                      <TableCell>Recorrência</TableCell>
                      <TableCell>{i18n.t("campaigns.table.contactList")}</TableCell>
                      <TableCell>{i18n.t("campaigns.table.whatsapp")}</TableCell>
                      <TableCell>{i18n.t("campaigns.table.scheduledAt")}</TableCell>
                      <TableCell>Próxima Execução</TableCell>
                      <TableCell>{i18n.t("campaigns.table.completedAt")}</TableCell>
                      <TableCell>{i18n.t("campaigns.table.confirmation")}</TableCell>
                      <TableCell align="right">{i18n.t("campaigns.table.actions")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <>
                      {campaigns.map((campaign) => (
                        <TableRow key={campaign.id}>
                          <TableCell>
                            <div className={classes.nameCell}>
                              {campaign.isRecurring && (
                                <Tooltip title="Campanha Recorrente">
                                  <RepeatIcon color="primary" fontSize="small" />
                                </Tooltip>
                              )}
                              {campaign.name}
                            </div>
                          </TableCell>
                          <TableCell>
                            <StatusBadge
                              color={getStatusTone(campaign.status)}
                              label={formatStatus(campaign.status)}
                            />
                          </TableCell>
                          <TableCell>
                            {campaign.isRecurring ? (
                              <div className={classes.recurrenceStack}>
                                <StatusBadge
                                  color="brand"
                                  dot={false}
                                  label={formatRecurrenceType(campaign.recurrenceType)}
                                />
                                <span className={classes.mutedMeta}>
                                  {campaign.executionCount || 0} execuções
                                </span>
                              </div>
                            ) : (
                              <StatusBadge color="neutral" dot={false} label="Única" />
                            )}
                          </TableCell>
                          <TableCell>
                            {campaign.contactListId
                              ? campaign.contactList?.name || "Lista removida"
                              : campaign.tagListId && campaign.tagListId !== "Nenhuma"
                              ? `Tag: ${campaign.tagListId}`
                              : <span className={classes.muted}>Não definida</span>}
                          </TableCell>
                          <TableCell>
                            {campaign.whatsappId
                              ? campaign.whatsapp?.name || "WhatsApp removido"
                              : <span className={classes.muted}>Não definido</span>}
                          </TableCell>
                          <TableCell className={classes.nowrap}>
                            {campaign.scheduledAt
                              ? datetimeToClient(campaign.scheduledAt)
                              : <span className={classes.muted}>Sem agendamento</span>}
                          </TableCell>
                          <TableCell>
                            {campaign.isRecurring && campaign.nextScheduledAt ? (
                              <div className={classes.inlineRow}>
                                <ScheduleIcon className={classes.inlineIcon} />
                                {datetimeToClient(campaign.nextScheduledAt)}
                              </div>
                            ) : campaign.isRecurring && campaign.status === 'FINALIZADA' ? (
                              <span className={classes.mutedMeta}>
                                Recorrência finalizada
                              </span>
                            ) : (
                              <span className={classes.muted}>—</span>
                            )}
                          </TableCell>
                          <TableCell className={classes.nowrap}>
                            {campaign.completedAt
                              ? datetimeToClient(campaign.completedAt)
                              : <span className={classes.muted}>Não concluída</span>}
                          </TableCell>
                          <TableCell>
                            {campaign.confirmation ? "Habilitada" : "Desabilitada"}
                          </TableCell>
                          <TableCell align="right">
                            <div className={classes.actionGroup}>
                              <Tooltip title="Relatório">
                                <IconButton
                                  aria-label={`Relatório de ${campaign.name}`}
                                  onClick={() =>
                                    history.push(`/campaign/${campaign.id}/report`)
                                  }
                                  size="small"
                                >
                                  <DescriptionIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Editar">
                                <IconButton
                                  aria-label={`Editar ${campaign.name}`}
                                  size="small"
                                  onClick={() => handleEditCampaign(campaign)}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Mais ações">
                                <IconButton
                                  aria-label={`Mais ações de ${campaign.name}`}
                                  aria-haspopup="true"
                                  size="small"
                                  onClick={(e) =>
                                    setRowMenu({ anchorEl: e.currentTarget, campaign })
                                  }
                                >
                                  <MoreVertIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                      {!loading && campaigns.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={10} style={{ borderBottom: "none" }}>
                            <EmptyState
                              icon={<SendIcon />}
                              title="Nenhuma campanha encontrada"
                              message={
                                searchParam || statusFilter || recurrenceFilter
                                  ? "Nenhuma campanha encontrada com os filtros selecionados."
                                  : "Crie uma campanha para enviar mensagens a uma lista de contatos."
                              }
                              action={
                                !(searchParam || statusFilter || recurrenceFilter) && (
                                  <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<AddIcon />}
                                    onClick={handleOpenCampaignModal}
                                  >
                                    {i18n.t("campaigns.buttons.add")}
                                  </Button>
                                )
                              }
                            />
                          </TableCell>
                        </TableRow>
                      )}
                      {loading && <TableRowSkeleton columns={10} />}
                    </>
                  </TableBody>
                </Table>
              </div>

              <div className={classes.footer}>
                <span className={classes.footerTotal}>Total: {totalCount} campanhas</span>
                <TablePagination
                  component="div"
                  count={totalCount}
                  page={pageNumber - 1} // Material-UI usa índice baseado em 0
                  onPageChange={handlePageChange}
                  rowsPerPage={pageSize}
                  onRowsPerPageChange={handlePageSizeChange}
                  rowsPerPageOptions={[5, 10, 25, 50, 100]}
                  labelRowsPerPage="Linhas por página:"
                  labelDisplayedRows={({ from, to, count }) =>
                    `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
                  }
                />
              </div>
            </Paper>

            <Menu
              anchorEl={rowMenu.anchorEl}
              open={Boolean(rowMenu.anchorEl)}
              onClose={closeRowMenu}
              getContentAnchorEl={null}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              {rowMenu.campaign?.status === "EM_ANDAMENTO" && (
                <MenuItem
                  onClick={() => {
                    const campaign = rowMenu.campaign;
                    closeRowMenu();
                    cancelCampaign(campaign);
                  }}
                >
                  <ListItemIcon className={classes.menuIcon}>
                    <PauseCircleOutlineIcon fontSize="small" />
                  </ListItemIcon>
                  Parar Campanha
                </MenuItem>
              )}
              {rowMenu.campaign?.status === "CANCELADA" && (
                <MenuItem
                  onClick={() => {
                    const campaign = rowMenu.campaign;
                    closeRowMenu();
                    restartCampaign(campaign);
                  }}
                >
                  <ListItemIcon className={classes.menuIcon}>
                    <PlayCircleOutlineIcon fontSize="small" />
                  </ListItemIcon>
                  Reiniciar Campanha
                </MenuItem>
              )}
              {rowMenu.campaign?.isRecurring && rowMenu.campaign?.status !== "FINALIZADA" && (
                <MenuItem
                  onClick={() => {
                    setSelectedCampaign(rowMenu.campaign);
                    setStopRecurrenceModalOpen(true);
                    closeRowMenu();
                  }}
                >
                  <ListItemIcon className={classes.menuIcon}>
                    <StopIcon fontSize="small" />
                  </ListItemIcon>
                  Interromper Recorrência
                </MenuItem>
              )}
              <MenuItem
                className={classes.menuDanger}
                onClick={() => {
                  setConfirmModalOpen(true);
                  setDeletingCampaign(rowMenu.campaign);
                  closeRowMenu();
                }}
              >
                <ListItemIcon className={classes.menuIcon}>
                  <DeleteOutlineIcon fontSize="small" />
                </ListItemIcon>
                Excluir
              </MenuItem>
            </Menu>
          </>}
    </MainContainer>
  );
};

export default Campaigns;
